import React from 'react';
import { motion } from 'framer-motion';
import { ExclamationTriangleIcon, ArrowPathIcon, HomeIcon } from '@heroicons/react/24/outline';

const isDynamicImportFetchError = (error) =>
  error?.name === 'TypeError' &&
  /Failed to fetch dynamically imported module|Importing a module script failed/i.test(
    error.message || ''
  );

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
    if (typeof window !== 'undefined' && window.console && process.env.NODE_ENV !== 'production') {
      window.console.error('[ErrorBoundary]', error, errorInfo);
    }

    if (typeof window !== 'undefined' && isDynamicImportFetchError(error)) {
      try {
        const retryKey = `pulsalink:dynamic-import-retry:${window.location.pathname}`;
        const lastRetry = Number(window.sessionStorage.getItem(retryKey));
        const now = Date.now();

        if (!lastRetry || now - lastRetry >= 60_000) {
          window.sessionStorage.setItem(retryKey, String(now));
          window.location.reload();
        }
      } catch (storageError) {
        if (process.env.NODE_ENV !== 'production') {
          window.console.warn(
            '[ErrorBoundary] Could not schedule dynamic import recovery.',
            storageError
          );
        }
      }
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReload = () => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  handleHome = () => {
    if (typeof window !== 'undefined') {
      window.location.href = '/dashboard';
    }
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return typeof this.props.fallback === 'function'
          ? this.props.fallback(this.state.error, this.handleReset)
          : this.props.fallback;
      }

      return (
        <div className='flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-6'>
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
            className='max-w-lg w-full bg-white rounded-2xl shadow-xl border border-gray-100 p-8 text-center'
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.1, type: 'spring', stiffness: 200 }}
              className='mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-orange-100 to-red-100'
            >
              <ExclamationTriangleIcon className='h-10 w-10 text-orange-500' />
            </motion.div>

            <h1 className='text-2xl font-bold text-gray-900 mb-2'>Ups! Ada yang tidak beres</h1>

            <p className='text-gray-500 mb-6 leading-relaxed'>
              Maaf, terjadi kesalahan tak terduga pada aplikasi. Tim kami telah mencatat masalah
              ini. Silakan coba muat ulang atau kembali ke beranda.
            </p>

            {this.state.error && (
              <details className='mb-6 text-left bg-gray-50 rounded-lg p-3 text-xs text-gray-600 border border-gray-200'>
                <summary className='cursor-pointer font-medium text-gray-700 hover:text-primary'>
                  Detail teknis (untuk admin)
                </summary>
                <pre className='mt-2 overflow-x-auto whitespace-pre-wrap break-words font-mono text-[11px]'>
                  {this.state.error?.toString()}
                </pre>
              </details>
            )}

            <div className='flex flex-col sm:flex-row gap-3 justify-center'>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={this.handleReload}
                className='inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-primary/20 hover:bg-primary-dark transition-colors'
              >
                <ArrowPathIcon className='h-4 w-4' />
                Muat Ulang
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={this.handleHome}
                className='inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 table-row-hover transition-colors'
              >
                <HomeIcon className='h-4 w-4' />
                Ke Dashboard
              </motion.button>
            </div>
          </motion.div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
